import { type ReactNode, type SubmitEvent, useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import { Checkbox } from "@lawline/ui/components/checkbox";
import { DialogFooter } from "@lawline/ui/components/dialog";
import { Input } from "@lawline/ui/components/input";
import { Label } from "@lawline/ui/components/label";

import {
	WEBHOOK_EVENT_TYPES,
	WEBHOOK_RUN_KINDS,
	type WebhookEndpointFields,
} from "../webhooks.types";
import { isHttpsUrl } from "../webhooks.utils";
import { WebhookErrorText } from "./webhook-error-text";

// Ticks one value in or out of a set that must keep at least one, as the server refuses an
// empty list.
function toggle<T>(values: readonly T[], value: T): T[] {
	if (!values.includes(value)) return [...values, value];
	return values.length === 1 ? [...values] : values.filter((v) => v !== value);
}

// The fields of an endpoint, for adding one and for editing one. The dialog unmounts it on
// close, so every open starts from `initial`.
export function WebhookEndpointForm({
	initial,
	pending,
	error,
	submitLabel,
	pendingLabel,
	secondary,
	onSubmit,
}: {
	initial: WebhookEndpointFields;
	pending: boolean;
	error: unknown;
	submitLabel: string;
	pendingLabel: string;
	secondary?: ReactNode;
	onSubmit: (fields: WebhookEndpointFields) => void;
}) {
	const t = useTranslations("webhooks.form");
	const tKinds = useTranslations("webhooks.kinds");
	const [url, setUrl] = useState(initial.url);
	const [description, setDescription] = useState(initial.description);
	const [eventTypes, setEventTypes] = useState(initial.eventTypes);
	const [runKinds, setRunKinds] = useState(initial.runKinds);
	const [touched, setTouched] = useState(false);
	const urlValid = isHttpsUrl(url);

	function submit(e: SubmitEvent) {
		e.preventDefault();
		setTouched(true);
		if (!urlValid) return;
		onSubmit({ url, description, eventTypes, runKinds });
	}

	return (
		<form onSubmit={submit} noValidate className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<Label htmlFor="webhook-url">{t("urlLabel")}</Label>
				<Input
					id="webhook-url"
					type="url"
					inputMode="url"
					placeholder="https://"
					value={url}
					onChange={(e) => setUrl(e.target.value)}
					onBlur={() => setTouched(true)}
					maxLength={2048}
					aria-invalid={touched && !urlValid}
					aria-describedby="webhook-url-hint"
				/>
				<p
					id="webhook-url-hint"
					className={
						touched && !urlValid ? "text-destructive text-xs" : "text-muted-foreground text-xs"
					}
				>
					{touched && !urlValid ? t("urlInvalid") : t("urlHint")}
				</p>
			</div>

			<div className="flex flex-col gap-2">
				<Label htmlFor="webhook-description">{t("descriptionLabel")}</Label>
				<Input
					id="webhook-description"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					maxLength={500}
				/>
			</div>

			<fieldset className="flex flex-col gap-2">
				<legend className="mb-2 text-sm font-medium">{t("eventsLabel")}</legend>
				{WEBHOOK_EVENT_TYPES.map((type) => (
					<Label key={type} className="font-mono text-xs font-normal">
						<Checkbox
							checked={eventTypes.includes(type)}
							onCheckedChange={() => setEventTypes((prev) => toggle(prev, type))}
						/>
						{type}
					</Label>
				))}
			</fieldset>

			<fieldset className="flex flex-col gap-2">
				<legend className="mb-2 text-sm font-medium">{t("kindsLabel")}</legend>
				{WEBHOOK_RUN_KINDS.map((kind) => (
					<Label key={kind} className="font-normal">
						<Checkbox
							checked={runKinds.includes(kind)}
							onCheckedChange={() => setRunKinds((prev) => toggle(prev, kind))}
						/>
						{tKinds(kind)}
					</Label>
				))}
			</fieldset>

			{error ? <WebhookErrorText error={error} /> : null}

			<DialogFooter>
				{secondary}
				<Button type="submit" disabled={pending}>
					{pending ? pendingLabel : submitLabel}
				</Button>
			</DialogFooter>
		</form>
	);
}
